import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft } from "lucide-react";
import logo from "../../images/Nkenne Ai-01 2.png";
import loginIllustration from "../../images/Learning language with friend.png";
import { forgotPassword } from "../../services/authService";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Email is required", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      toast.error("Please enter a valid email address", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Call the forgot password API
      const response = await forgotPassword(email);
      
      console.log("Forgot password response:", response);
      
      toast.success(response.message || "Password reset link sent to your email!", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setIsEmailSent(true);
    } catch (error) {
      console.error("Forgot password error:", error);
      toast.error(error.message || "Something went wrong. Please try again.", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="min-h-screen flex flex-col md:flex-row">
        {/* Left Side - Illustration */}
        <div className="md:flex-1 bg-secondary flex items-center justify-center p-4 md:p-8 hidden md:flex">
          <img
            src={loginIllustration}
            alt="Illustration"
            className="max-w-[70%] h-auto"
          />
        </div>

        {/* Right Side - Confirmation */}
        <div className="flex-1 bg-white flex items-center justify-center p-4 md:p-8">
          <div className="w-full max-w-md space-y-6">
            {/* Logo and Branding */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <img src={logo} alt="Logo" className="w-32 md:w-50 md:h-50" />
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">
                Reset your password
              </h1>
            </div>

            {/* Confirmation Card */}
            <div className="space-y-6 border p-6 rounded-xl">
              <div className="text-center">
                <p className="text-sm text-gray-900 mb-6">
                  Check your email for a link to reset your password. If it doesn't appear within a few minutes, check your spam folder.
                </p>
                
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm text-secondary hover:text-teal-500 transition-colors font-bold"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to sign in
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left Side - Illustration */}
      <div className="md:flex-1 bg-secondary flex items-center justify-center p-4 md:p-8 hidden md:flex">
        <img
          src={loginIllustration}
          alt="Illustration"
          className="max-w-[70%] h-auto"
        />
      </div>

      {/* Right Side - Forgot Password Form */}
      <div className="flex-1 bg-white flex items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-md space-y-6">
          {/* Logo and Branding */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src={logo} alt="Logo" className="w-32 md:w-50 md:h-50" />
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">
              Reset your password
            </h1>
          </div>

          <div className="space-y-6 border p-6 rounded-xl">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Input */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-bold text-gray-700 mb-1"
                >
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-sm"
                  placeholder="Enter your email address"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-secondary hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 text-sm"
              >
                {isLoading ? "Sending reset instructions..." : "Send reset instructions"}
              </button>
            </form>

            {/* Back to Sign In */}
            <div className="text-center">
              <Link
                to="/login"
                className="inline-flex items-center text-sm text-gray-600 hover:text-secondary transition-colors font-bold"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
